import { useState } from "react";
import { format, subMonths, addMonths } from "date-fns";
import { ptBR } from "date-fns/locale";
import { 
  ChevronLeft, 
  ChevronRight, 
  FileText, 
  FileSpreadsheet,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  PieChart,
  BarChart3,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useReports } from "@/hooks/use-reports";
import { exportTransactionsToCSV, exportReportToPDF } from "@/lib/export-utils";
import { toast } from "@/hooks/use-toast";
import * as LucideIcons from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";

const Reports = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  
  const report = useReports(selectedMonth);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);

  const handlePreviousMonth = () => {
    setSelectedMonth(prev => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    const next = addMonths(selectedMonth, 1);
    if (next <= new Date()) {
      setSelectedMonth(next);
    }
  };

  const handleExportCSV = () => {
    if (report.transactions.length === 0) {
      toast({ title: "Nenhuma transação para exportar", variant: "destructive" });
      return;
    }
    const filename = `transacoes-${format(selectedMonth, "yyyy-MM")}`;
    exportTransactionsToCSV(report.transactions, filename);
    toast({ title: "CSV exportado com sucesso!" });
  };

  const handleExportPDF = () => {
    if (report.transactions.length === 0) {
      toast({ title: "Nenhum dado para exportar", variant: "destructive" });
      return;
    }
    exportReportToPDF(report);
    toast({ title: "PDF exportado com sucesso!" });
  };

  const getIcon = (iconName: string) => {
    const Icon = (LucideIcons as any)[iconName] || LucideIcons.CircleDot;
    return Icon;
  };

  const monthLabel = format(selectedMonth, "MMMM yyyy", { locale: ptBR });
  const capitalizedMonth = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);

  // Chart configs
  const categoryChartConfig = Object.fromEntries(
    report.categorySummaries.map(cat => [cat.id, { label: cat.name, color: cat.color }])
  );

  const dailyChartConfig = {
    amount: { label: "Gastos", color: "hsl(var(--primary))" },
  };

  const dayOfWeekData = report.spendingPatterns.highestSpendingDay
    ? [
        { day: "Dom", amount: 0 },
        { day: "Seg", amount: 0 },
        { day: "Ter", amount: 0 },
        { day: "Qua", amount: 0 },
        { day: "Qui", amount: 0 },
        { day: "Sex", amount: 0 },
        { day: "Sáb", amount: 0 },
      ]
    : [];

  // Populate day of week data
  if (report.transactions.length > 0) {
    report.transactions
      .filter(t => t.type === "expense")
      .forEach(t => {
        const dayIndex = new Date(t.transaction_date).getDay();
        if (dayOfWeekData[dayIndex]) {
          dayOfWeekData[dayIndex].amount += Number(t.amount);
        }
      });
  }

  if (report.isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div className="space-y-4 sm:space-y-0 sm:flex sm:flex-wrap sm:items-center sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Relatórios</h1>
          <p className="text-sm text-muted-foreground">Análise detalhada das suas finanças</p>
        </div>

        {/* Month Selector */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="min-h-[44px] min-w-[44px]" onClick={handlePreviousMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg min-w-[140px] sm:min-w-[180px] justify-center">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium text-sm sm:text-base">{capitalizedMonth}</span>
          </div>
          <Button
            variant="outline"
            size="icon"
            className="min-h-[44px] min-w-[44px]"
            onClick={handleNextMonth}
            disabled={addMonths(selectedMonth, 1) > new Date()}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Export Buttons */}
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button variant="outline" size="sm" className="flex-1 sm:flex-none min-h-[44px] sm:min-h-0" onClick={handleExportCSV}>
            <FileSpreadsheet className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">CSV</span>
          </Button>
          <Button variant="default" size="sm" className="flex-1 sm:flex-none min-h-[44px] sm:min-h-0" onClick={handleExportPDF}>
            <FileText className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">PDF</span>
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2 p-4 sm:p-6">
            <CardDescription>Receitas</CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
            <div className="text-lg sm:text-2xl font-bold text-success truncate">
              {formatCurrency(report.totalIncome)}
            </div>
            <div className="flex items-center gap-1 mt-1">
              {report.previousMonthComparison.incomeChangePercent > 0 ? (
                <TrendingUp className="h-3 w-3 text-success flex-shrink-0" />
              ) : report.previousMonthComparison.incomeChangePercent < 0 ? (
                <TrendingDown className="h-3 w-3 text-destructive flex-shrink-0" />
              ) : (
                <Minus className="h-3 w-3 text-muted-foreground flex-shrink-0" />
              )}
              <span className="text-xs text-muted-foreground truncate">
                {report.previousMonthComparison.incomeChangePercent.toFixed(1)}% <span className="hidden sm:inline">vs mês anterior</span>
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 p-4 sm:p-6">
            <CardDescription>Despesas</CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
            <div className="text-lg sm:text-2xl font-bold text-destructive truncate">
              {formatCurrency(report.totalExpenses)}
            </div>
            <div className="flex items-center gap-1 mt-1">
              {report.previousMonthComparison.expenseChangePercent > 0 ? (
                <TrendingUp className="h-3 w-3 text-destructive flex-shrink-0" />
              ) : report.previousMonthComparison.expenseChangePercent < 0 ? (
                <TrendingDown className="h-3 w-3 text-success flex-shrink-0" />
              ) : (
                <Minus className="h-3 w-3 text-muted-foreground flex-shrink-0" />
              )}
              <span className="text-xs text-muted-foreground truncate">
                {report.previousMonthComparison.expenseChangePercent.toFixed(1)}% <span className="hidden sm:inline">vs mês anterior</span>
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 p-4 sm:p-6">
            <CardDescription>Saldo do Mês</CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
            <div className={`text-lg sm:text-2xl font-bold truncate ${report.balance >= 0 ? "text-success" : "text-destructive"}`}>
              {formatCurrency(report.balance)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Receitas - Despesas
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 p-4 sm:p-6">
            <CardDescription>Transações</CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
            <div className="text-lg sm:text-2xl font-bold">
              {report.transactions.length}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              transações registradas
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Daily Spending Chart */}
        <Card>
          <CardHeader className="pb-2 p-4 sm:p-6 sm:pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Gastos Diários
            </CardTitle>
            <CardDescription>Evolução dos gastos ao longo do mês</CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
            {report.dailySpending.length > 0 ? (
              <ChartContainer config={dailyChartConfig} className="h-[180px] sm:h-[250px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={report.dailySpending} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="dailyGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 9 }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(val) => format(new Date(val), "dd")}
                    />
                    <YAxis
                      tick={{ fontSize: 9 }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(val) => `${(val / 1000).toFixed(0)}k`}
                    />
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          formatter={(value) => formatCurrency(Number(value))}
                          labelFormatter={(label) => format(new Date(label), "dd/MM/yyyy")}
                        />
                      }
                    />
                    <Area
                      type="monotone"
                      dataKey="amount"
                      name="Gastos"
                      stroke="hsl(var(--primary))"
                      fill="url(#dailyGradient)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartContainer>
            ) : (
              <div className="h-[180px] sm:h-[250px] flex items-center justify-center text-muted-foreground">
                Nenhum dado disponível
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category Distribution */}
        <Card>
          <CardHeader className="pb-2 p-4 sm:p-6 sm:pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Distribuição por Categoria
            </CardTitle>
            <CardDescription>Onde você mais gastou</CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
            {report.categorySummaries.length > 0 ? (
              <ChartContainer config={categoryChartConfig} className="h-[200px] sm:h-[280px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={report.categorySummaries.slice(0, 6)}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={65}
                      paddingAngle={2}
                      dataKey="totalAmount"
                      nameKey="name"
                    >
                      {report.categorySummaries.slice(0, 6).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} strokeWidth={0} />
                      ))}
                    </Pie>
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          formatter={(value) => formatCurrency(Number(value))}
                        />
                      }
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </ChartContainer>
            ) : (
              <div className="h-[200px] sm:h-[280px] flex items-center justify-center text-muted-foreground">
                Nenhum dado disponível
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Category Details */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-lg">Detalhamento por Categoria</CardTitle>
          <CardDescription>Análise completa dos gastos por categoria</CardDescription>
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
          {report.categorySummaries.length > 0 ? (
            <div className="space-y-4">
              {report.categorySummaries.map((category) => {
                const Icon = getIcon(category.icon);
                return (
                  <div key={category.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: `${category.color}20` }}
                        >
                          <Icon className="h-4 w-4" style={{ color: category.color }} />
                        </div>
                        <div>
                          <p className="font-medium">{category.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {category.transactionCount} transações • Média: {formatCurrency(category.averageTransaction)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{formatCurrency(category.totalAmount)}</p>
                        <p className="text-xs text-muted-foreground">{category.percentage.toFixed(1)}%</p>
                      </div>
                    </div>
                    <Progress value={category.percentage} className="h-2" />
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Nenhuma despesa categorizada neste mês
            </p>
          )}
        </CardContent>
      </Card>

      {/* Spending Patterns */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Day of Week Analysis */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg">Padrão Semanal</CardTitle>
            <CardDescription>Gastos por dia da semana</CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
            {dayOfWeekData.some(d => d.amount > 0) ? (
              <ChartContainer config={{ amount: { label: "Gastos", color: "hsl(var(--primary))" } }} className="h-[160px] sm:h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dayOfWeekData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 9 }} tickLine={false} axisLine={false} tickFormatter={(val) => `${(val / 1000).toFixed(0)}k`} />
                    <ChartTooltip content={<ChartTooltipContent formatter={(value) => formatCurrency(Number(value))} />} />
                    <Bar dataKey="amount" name="Gastos" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            ) : (
              <div className="h-[160px] sm:h-[200px] flex items-center justify-center text-muted-foreground">
                Dados insuficientes
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-4 pt-4 border-t">
              <div>
                <p className="text-xs text-muted-foreground">Dia com mais gastos</p>
                <p className="font-medium">{report.spendingPatterns.highestSpendingDay?.dayName || "-"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Gasto médio diário</p>
                <p className="font-medium">{formatCurrency(report.dailySpending.length > 0 ? report.totalExpenses / report.dailySpending.filter(d => d.amount > 0).length : 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Spending Insights */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg">Insights</CardTitle>
            <CardDescription>Resumo do seu comportamento financeiro</CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0 space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <span className="text-sm">Maior gasto único</span>
              <span className="font-bold">{formatCurrency(report.spendingPatterns.largestTransactions[0]?.amount || 0)}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <span className="text-sm">Média por transação</span>
              <span className="font-bold">
                {formatCurrency(report.transactions.length > 0 ? report.totalExpenses / report.transactions.filter(t => t.type === 'expense').length : 0)}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <span className="text-sm">Taxa de poupança</span>
              <span className={`font-bold ${report.balance >= 0 ? 'text-success' : 'text-destructive'}`}>
                {report.totalIncome > 0 ? ((report.balance / report.totalIncome) * 100).toFixed(1) : 0}%
              </span>
            </div>
            {report.categorySummaries[0] && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <span className="text-sm">Categoria principal</span>
                <span className="font-bold">{report.categorySummaries[0].name}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Reports;
